import jwtDecode from 'jwt-decode';
import {
  PRIMARY_PRODUCT_LINE,
  SECONDARY_PRODUCT_LINE,
  SECONDARY_PRODUCT_LINES,
} from '../constants/appConstants';
import { getIdFromUrl } from './appUtil';

/**
 * Extracts the trailing id from a url or bare id, discarding any query string,
 * fragment, or trailing slashes so the two sides compare cleanly.
 */
function trailingUserId(urlOrId?: string | null) {
  if (!urlOrId) {
    return '';
  }

  const withoutQueryOrFragment = urlOrId.split(/[?#]/)[0];
  return getIdFromUrl(withoutQueryOrFragment.replace(/\/+$/, ''));
}

/**
 * Determines whether an admin user record is the logged in user's own profile.
 * Users are not allowed to edit or deactivate themselves.
 *
 * Both sides are the same id space: the route param is the `UserDto.id` the list
 * navigated to, and `state.userUrl` is that same record's `UserDto.url`. Only the
 * trailing id is compared, which is safe because the web client always
 * authenticates as a user - it never holds a driver url in `state.userUrl`.
 *
 * @param loggedInUserUrl The url of the logged in user, held in `state.userUrl`
 * @param recordId The id of the user record being viewed
 * @returns true only when both values are present and identify the same user
 */
export function isOwnUserProfile(loggedInUserUrl?: string | null, recordId?: string | null) {
  const loggedInUserId = trailingUserId(loggedInUserUrl);
  const viewedUserId = trailingUserId(recordId);
  return !!loggedInUserId && !!viewedUserId && loggedInUserId === viewedUserId;
}

/**
 * "Last, First" for display, falling back to whichever part exists and then to the
 * username.
 *
 * The same shape as adminUtil's `getFullName` for a complete record, but silent:
 * getFullName records a warning whenever a part is missing, and this is called per row
 * on every render of a table, so an incomplete record there would log continuously.
 */
export function userDisplayName(user?: {
  firstName?: string;
  lastName?: string;
  username?: string;
}): string {
  const parts = [user?.lastName, user?.firstName].filter(part => !!part?.trim());
  return parts.length > 0 ? parts.join(', ') : (user?.username ?? '');
}

export function getPrimaryProductLineFromToken(token: string) {
  try {
    const { primaryProductLine } = jwtDecode(token);
    localStorage.setItem(PRIMARY_PRODUCT_LINE, primaryProductLine);
    return primaryProductLine;
  } catch (e) {
    return null;
  }
}

export function getSecondaryProductLineFromToken(token: string) {
  try {
    const { secondaryProductLine } = jwtDecode(token);
    localStorage.setItem(SECONDARY_PRODUCT_LINE, secondaryProductLine);
    return secondaryProductLine;
  } catch (e) {
    return null;
  }
}

export function getSecondaryProductLinesFromToken(token: string) {
  try {
    const { secondaryProductLines } = jwtDecode(token);
    localStorage.setItem(SECONDARY_PRODUCT_LINES, secondaryProductLines);
    return secondaryProductLines;
  } catch (e) {
    return null;
  }
}
